import type { CharacterState } from '../character/character-state';
import type { Facing } from '../contracts/presentation-snapshot';
import type { InputFrame } from '../input/input-frame';
import type { AttackDefinition } from './attack';

export const COMBAT_RESULT_TYPES = ['IGNORED_INVINCIBLE', 'COUNTERED', 'GUARDED', 'HIT'] as const;
export type CombatResultType = (typeof COMBAT_RESULT_TYPES)[number];

export interface PendingHit {
  readonly logicalFrame: number;
  readonly attacker: 0 | 1;
  readonly defender: 0 | 1;
  readonly attackerId: string;
  readonly defenderId: string;
  readonly attackInstanceId: string;
  readonly attackId: string;
  readonly hitId: string;
  readonly source: 'ATTACK' | 'TRAP';
  readonly sourceInstanceId: string;
  readonly attackFacing: Facing;
}

export interface CombatResult extends PendingHit {
  readonly result: CombatResultType;
  readonly attack: AttackDefinition;
  readonly damage: number;
  readonly hitstopFrames: number;
}

export interface DefenseContext {
  readonly state: CharacterState;
  readonly facing: Facing;
  readonly input: InputFrame;
  readonly invincible: boolean;
  readonly counterWindowActive: boolean;
}

const GUARD_CAPABLE_STATES: readonly CharacterState[] = ['Idle', 'Walk', 'Crouch', 'Guard'];

export const isBackHeld = (input: InputFrame, facing: Facing): boolean => {
  if (input.left === input.right) return false;
  return facing === 'RIGHT' ? input.left : input.right;
};

export const canGuard = (attack: AttackDefinition, defender: DefenseContext): boolean =>
  attack.guardable
  && GUARD_CAPABLE_STATES.includes(defender.state)
  && (defender.input.guard || isBackHeld(defender.input, defender.facing));

export const classifyPendingHit = (
  pending: PendingHit,
  attack: AttackDefinition,
  defender: DefenseContext,
): CombatResult => {
  const result: CombatResultType = defender.invincible
    ? 'IGNORED_INVINCIBLE'
    : defender.counterWindowActive && attack.counterable
      ? 'COUNTERED'
      : canGuard(attack, defender)
        ? 'GUARDED'
        : 'HIT';
  return Object.freeze({
    ...pending,
    result,
    attack,
    damage: result === 'GUARDED' ? attack.chipDamage : result === 'HIT' ? attack.damage : 0,
    hitstopFrames: result === 'GUARDED' ? attack.blockHitstopFrames : result === 'HIT' ? attack.hitstopFrames : 0,
  });
};

export const comboScaleForPriorHits = (priorHits: number): number => {
  if (!Number.isInteger(priorHits) || priorHits < 0) throw new RangeError('priorHits must be a non-negative integer.');
  if (priorHits === 0) return 1;
  if (priorHits === 1) return 0.95;
  if (priorHits === 2) return 0.9;
  return 0.85;
};

export const calculateScaledDamage = (baseDamage: number, priorHits: number): number => {
  if (!Number.isFinite(baseDamage) || baseDamage < 0) throw new RangeError('baseDamage must be finite and non-negative.');
  return Math.floor(baseDamage * comboScaleForPriorHits(priorHits));
};

export const sortPendingHitsCanonically = (hits: readonly PendingHit[]): PendingHit[] => [...hits].sort((left, right) =>
  left.attacker - right.attacker
  || left.defender - right.defender
  || left.attackInstanceId.localeCompare(right.attackInstanceId)
  || left.hitId.localeCompare(right.hitId)
  || left.sourceInstanceId.localeCompare(right.sourceInstanceId));
